/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { AppError } from "@tivocorp/data-domain-lib";

import {
  ContentDetailInteractor,
  EpisodeGuideInteractor,
  PostSigninInteractor,
  PreviewInteractor,
  RecordingsServiceInteractor,
  ShowflixCarouselInteractor,
  UpcomingOffersInteractor,
  VodInteractor,
  MyShowsInteractor,
  RecordingsStatusInteractor
} from "../../model/interactor";
import {
  Log,
  DiagnosticLogger,
  AnalyticsLogger,
  OperationalLogger
} from "../../model/logging";
import { Locale, LocaleKeys } from "../../model/locale";
import { selectPreviousUrl } from "../screens";
import { showToastMessage } from "../toastMessage";
import {
  ActionKernelType,
  ContentScreenType,
  RecordingStatusV2,
  DiagnosticSeverity
} from "../../model/types";
import type {
  EpisodeInfo,
  SeasonsData,
  TPreview,
  UpcomingOfferListV2
} from "../../model/types";
import { buildPosterImageUrl } from "../../model/utility";
import { recordingActionErrorMapper } from "../../model/errorMapper/ScheduleActionErrorMappers";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import {
  applyContentDetailScheduleSuccess,
  buildContentDetailRecordingOptionGroups,
  buildContentDetailPlayable,
  getContentDetailFavoriteTarget,
  readContentDetailActionString
} from "./contentDetailActions";
import {
  LEGACY_RECORDING_STATUS,
  recordingScheduled
} from "../utility/recordingEvents";
import { getImageHostBaseUrl, PreviewFormatter } from "../utility";
import { buildAtmosphericImageUrl } from "./atmosphericImageUrlBuilder";
import type { TAtmosphericContentType } from "./atmosphericImageUrlBuilder";
import {
  ContentDetailErrorMapper,
  getContentDetailSheetErrorMessage
} from "./contentDetailErrorMapper";
import {
  formatClockTime,
  formatGenres,
  formatNextAiringText,
  buildBadges,
  mapContentScreenDetailsToState,
  mapUnifiedCollectionScreenDetailsToState,
  applyContentDetailStreamingOptions
} from "./contentDetailMappers";
import {
  deriveUpcomingSeasons,
  mapEpisode,
  mapMoreLikeThisItem,
  mapSeasons,
  mapUpcomingOffer
} from "./contentDetailSectionMappers";
import {
  getContentDetailSheetTypeLabel,
  getSheetMainTitle,
  getSectionErrorMessage
} from "./contentDetailStrings";
import type {
  ContentDetailActionData,
  ContentDetailEpisodeData,
  ContentDetailEpisodeGuideState,
  ContentDetailFocusedRow,
  ContentDetailKind,
  LoadContentDetailSheetParams,
  ContentDetailRecommendedData,
  ContentDetailRecordingParams,
  ContentDetailRecordingOptionGroup,
  ContentDetailSeasonData,
  ContentDetailSectionArea,
  ContentDetailSectionData,
  ContentDetailSectionId,
  ContentDetailSectionState,
  ContentDetailSeed,
  ContentDetailSheetState,
  ContentDetailState,
  ContentDetailStreamingServiceData,
  ContentDetailUpcomingData,
  ContentDetailUpcomingState
} from "./contentDetail.types";
import type { UnityRootState } from "../UnityAppStore";
import { CONTENT_DETAIL_SEED_TYPE } from "./contentDetail.types";
import { MILLISECONDS_IN_A_SECOND, SECONDS_IN_A_MINUTE } from "../../config";

const TAG: string = "contentDetailSlice";

const ATMOSPHERIC_IMAGE_WIDTH: number = 1920;
const ATMOSPHERIC_IMAGE_HEIGHT: number = 1080;

/**
 * Edge of one square streaming-provider tile, in 1920×1080 design pixels
 * (Figma `2198:5717` — 75×75). Kept in sync with
 * `$watch-actions-row-logo-size` in WatchActionsRow.scss.
 */
const PROVIDER_LOGO_SIZE: number = 75;

/**
 * Page size for the Episodes rail. Copied from Android's
 * `EPISODE_GUIDE_PAGE_SIZE` so both clients page identically — a page is
 * "full", and therefore possibly followed by another, exactly when
 * `page.length === EPISODE_GUIDE_PAGE_SIZE`.
 */
export const EPISODE_GUIDE_PAGE_SIZE: number = 50;

/** Page size for the Upcoming rail. Android's `UPCOMING_OFFERS_LIMIT`. */
export const UPCOMING_OFFERS_PAGE_SIZE: number = 20;

/** Page size for the May Also Like rail. Android's `MORE_LIKE_THIS_LIMIT`. */
export const MORE_LIKE_THIS_PAGE_SIZE: number = 20;

/**
 * Fire the next page once focus reaches `items.length - 1 - 4`. Android's
 * `EPISODE_GUIDE_ITEMS_PAGINATION_THRESHOLD` / `UPCOMING_ITEMS_PAGINATION_THRESHOLD`.
 */
export const PAGINATION_THRESHOLD: number = 4;

/** screen_id for Content Detail analytics events. */
export const CONTENT_DETAIL_SCREEN_ID: string = "content_details";

/** screen_name for Content Detail analytics events. */
export const CONTENT_DETAIL_SCREEN_NAME: string = "ContentDetails";

/** contentHighlighted dwell threshold for Content Detail's rails.*/
export const CONTENT_DETAIL_HIGHLIGHT_DWELL_MS: number = 400;

/** Shared analytics logger for all Content Detail screen events. */
export const ContentDetailAnalyticsLogger = AnalyticsLogger.create({
  screenId: CONTENT_DETAIL_SCREEN_ID,
  screenName: CONTENT_DETAIL_SCREEN_NAME
});

/**
 * previous_screen_id for `viewedScreen`, captured at mount (by
 * {@link captureContentDetailEntryScreen}) before Redux's previousUrl
 * advances to this screen's own url.
 */
let contentDetailEntryScreenId: string = "";

/** Captures the entry screen id for `viewedScreen`; dispatch on mount. */
export const captureContentDetailEntryScreen = createAsyncThunk<
  void,
  void,
  { state: UnityRootState }
>(
  "contentDetail/captureContentDetailEntryScreen",
  (_: void, { getState }): void => {
    contentDetailEntryScreenId = selectPreviousUrl(getState());
  }
);

/**
 * previous_screen_id for the interaction events (`contentSelected`,
 * `viewedContentDetails`, `itemSelected`) logged from the view layer — same
 * captured value `viewedScreen` uses, exposed as a plain getter since those
 * calls happen outside a thunk.
 */
export function getContentDetailEntryScreenId(): string {
  return contentDetailEntryScreenId;
}

/**
 * viewedScreen — fired on exit only, using the previous-screen id captured
 * at mount, so it reports where the viewer came from rather than Content
 * Detail's own url (which previousUrl would already show by unmount time).
 */
export const logContentDetailViewedScreen = createAsyncThunk<void, number>(
  "contentDetail/logContentDetailViewedScreen",
  (timeOnScreenMs: number): void => {
    ContentDetailAnalyticsLogger.logViewedScreen({
      previous_screen_id: contentDetailEntryScreenId,
      time_on_screen: String(timeOnScreenMs)
    });
  }
);

function emptySection<TItem>(): ContentDetailSectionState<TItem> {
  return {
    status: "hidden",
    items: [],
    hasMoreItems: false,
    isLoadingMore: false,
    error: null
  };
}

function emptyEpisodeGuide(): ContentDetailEpisodeGuideState {
  return {
    ...emptySection<ContentDetailEpisodeData>(),
    seasons: [],
    selectedSeasonOrYear: null,
    grouping: undefined,
    itemsBySeason: {},
    hasMoreBySeason: {},
    seasonScrollRequestToken: 0
  };
}

function emptyUpcoming(): ContentDetailUpcomingState {
  return {
    ...emptySection<ContentDetailUpcomingData>(),
    seasons: [],
    selectedSeasonOrYear: null,
    seasonScrollRequestToken: 0
  };
}

const INITIAL_STATE: ContentDetailState = {
  seed: null,
  kind: null,
  isLoading: false,
  isRefreshing: false,
  isRecordingPending: false,
  error: null,
  errorCode: "",

  starRating: null,
  genres: [],
  cast: [],

  watchActions: [],
  streamingServices: [],

  badges: [],

  scheduleActions: [],

  sections: [],
  episodeGuideObjectId: null,
  upcomingObjectId: null,
  mayAlsoLikeObjectId: null,

  episodeGuide: emptyEpisodeGuide(),
  upcoming: emptyUpcoming(),
  castMembers: emptySection(),
  mayAlsoLike: emptySection(),

  focusedRow: "watch",
  focusedActionIndex: 0,
  focusedSectionIndex: 0,
  focusedSectionArea: "rail",
  focusedItemIndex: 0,

  sheet: null
};

// ─── Screen-load thunk ───

/** Payload the `loadContentDetail` thunk resolves with on success. */
interface LoadContentDetailPayload {
  seed: ContentDetailSeed;
  data: Partial<ContentDetailState>;
}

/**
 * Payload the `loadContentDetail` thunk rejects with.
 *
 * `errorCode` is the A716 composite code (`"A716-G500"`, bare `"A716"`, or
 * `""`) that drives the error dialog — `""` for the pre-flight "missing seed
 * id" guard, which never reaches a service call and so is not an API
 * failure the catalog code should describe.
 */
interface LoadContentDetailErrorPayload {
  message: string;
  errorCode: string;
}

/** Extra argument accepted by `loadContentDetail`. */
export interface LoadContentDetailArgs extends ContentDetailSeed {
  /**
   * True when the screen already shows this title and is only re-reading it
   * (e.g. after a recording was scheduled). Keeps the current payload
   * on-screen and raises `isRefreshing` instead of `isLoading`.
   */
  isRefresh?: boolean;
}

/**
 * Maps a screen-details `contentScreenType` discriminator onto a
 * {@link ContentDetailKind}. Unknown types fall back to "program", the
 * generic non-movie, non-episode case.
 */
export function deriveContentKind(
  contentScreenType: string
): ContentDetailKind {
  switch (contentScreenType) {
    case ContentScreenType.EpisodeScreenDetails:
      return "episode";
    case ContentScreenType.MovieScreenDetails:
      return "movie";
    case ContentScreenType.GameScreenDetails:
      return "game";
    default:
      return "program";
  }
}

/**
 * Narrows a {@link ContentDetailKind} to the atmospheric image cases.
 *
 * Only the content-seed path calls this, and `deriveContentKind` never
 * returns "series" — the unified-collection path passes "series" to
 * `applyHeroImage` directly — so this maps episode vs everything-else.
 */
function toAtmosphericType(kind: ContentDetailKind): TAtmosphericContentType {
  return kind === "episode" ? "episode" : "movie";
}

/**
 * Resolves maturity rating + star rating from preview via PreviewFormatter
 * and mutates the target data object in place.
 */
function applyPreviewRatings(
  data: Partial<ContentDetailState>,
  preview: unknown
): void {
  const formatter: PreviewFormatter = new PreviewFormatter(
    preview !== null && preview !== undefined ? preview : undefined
  );
  const internalRatings: string[] = formatter.getInternalRatings();
  if (internalRatings.length > 0) {
    data.rating = internalRatings[0];
  }
  const formatterStarRating: number | null = formatter.getStarRating();
  if (formatterStarRating !== null) {
    data.starRating = formatterStarRating;
  }
}

/** Resolves each Watch pill's offer-subtitle `ratingKey` into its display name. */
function applyWatchSubtitleRatings(data: Partial<ContentDetailState>): void {
  if (data.watchActions === undefined) {
    return;
  }
  data.watchActions = data.watchActions.map(
    (action: ContentDetailActionData): ContentDetailActionData => {
      const subtitle = action.subtitle;
      if (
        subtitle === undefined ||
        subtitle.kind !== "offer" ||
        subtitle.ratingKey === undefined
      ) {
        return action;
      }
      const rating: string = PreviewInteractor.fetchInternalRatingName(
        subtitle.ratingKey
      );
      return {
        ...action,
        subtitle: { ...subtitle, rating: rating !== "" ? rating : undefined }
      };
    }
  );
}

/**
 * Composes the atmospheric hero image URL and assigns it to the data object
 * when valid. Returns without mutation when the composed URL is empty.
 */
function applyHeroImage(
  data: Partial<ContentDetailState>,
  imageHostBaseUrl: string,
  objectId: string,
  contentType: TAtmosphericContentType
): void {
  const heroImageUrl: string = buildAtmosphericImageUrl({
    baseImageUrl: imageHostBaseUrl,
    objectId,
    contentType,
    width: ATMOSPHERIC_IMAGE_WIDTH,
    height: ATMOSPHERIC_IMAGE_HEIGHT
  });
  if (heroImageUrl !== "") {
    data.heroImageUrl = heroImageUrl;
    data.heroImageAlt = data.title !== undefined ? data.title : "";
  }
}

/**
 * Composes each streaming provider's tile logo URL and rewrites
 * `data.streamingServices` with it.
 *
 * The preview payload carries only `providerId` + `displayName` (see the
 * `Provider` DTO), so the logo URL is *composed* rather than read: the
 * provider id is a `tivo:pt.*` partner id, and `imagetype=partnerSourceLogo`
 * is the image type the service serves that partner's branding under — the
 * same pairing the Guide uses for an app-linear channel's source logo. Without
 * this the row falls back to rendering the provider's name as text.
 *
 * The general image host, not the channel-logo one: partner branding is served
 * off the `/images` endpoint, and the channel-logo host exists for the
 * `/logos/{w}x{h}/{id}.png` form (see
 * doc/foundational/image-loading/image-loading-business-rules.md).
 *
 * A no-op when no image host resolved; the tiles then show their name
 * fallback rather than a broken image.
 */
function applyProviderLogos(
  data: Partial<ContentDetailState>,
  imageHostBaseUrl: string
): void {
  const services: ContentDetailStreamingServiceData[] | undefined =
    data.streamingServices;
  if (imageHostBaseUrl === "" || services === undefined) {
    return;
  }
  data.streamingServices = services.map(
    (
      service: ContentDetailStreamingServiceData
    ): ContentDetailStreamingServiceData => ({
      ...service,
      logoUrl: buildPosterImageUrl(
        imageHostBaseUrl,
        service.id,
        "PARTNER_LOGO",
        PROVIDER_LOGO_SIZE,
        PROVIDER_LOGO_SIZE
      )
    })
  );
}

/**
 * Resolves the image host base URL. Returns an empty string on failure
 * (non-fatal — images simply won't render).
 */
async function resolveImageHostBaseUrl(): Promise<string> {
  try {
    return await getImageHostBaseUrl(new PostSigninInteractor());
  } catch (imageHostError: unknown) {
    Log.e(
      TAG,
      "loadContentDetail() imageHost resolution failed",
      imageHostError
    );
    DiagnosticLogger.log(
      "content_detail_lifecycle",
      "loadContentDetail() imageHost resolution failed",
      DiagnosticSeverity.Warning,
      { event: "image_host_resolution_failed" }
    );
    return "";
  }
}

/**
 * Fetches the preview matching the seed's id namespace — each seed type has
 * its own preview endpoint, and sending e.g. an offer id to the content
 * endpoint answers RESOURCE_NOT_FOUND.
 *
 * Not used for `unifiedCollectionId`, which `loadContentDetail` handles on
 * its own branch.
 */
function fetchPreviewForSeed(
  previewInteractor: PreviewInteractor,
  seed: ContentDetailSeed,
  signal: AbortSignal
): Promise<TPreview> {
  switch (seed.seedType) {
    case "offerId":
      return previewInteractor.fetchOfferPreviewV4(
        { offerId: seed.seedId },
        true,
        { signal }
      );
    case "recordingId":
      return previewInteractor.fetchRecordingPreviewV3(
        { recordingId: seed.seedId },
        true,
        { signal }
      );
    case "vodOfferId":
      return previewInteractor.fetchVodOfferPreviewV2(
        { vodOfferId: seed.seedId },
        true,
        { signal }
      );
    default:
      return previewInteractor.fetchContentPreviewV3(
        { contentId: seed.seedId },
        true,
        { signal }
      );
  }
}

/**
 * Loads Content Detail data for the given seed.
 *
 * `offerId` / `recordingId` / `vodOfferId` seeds resolve through the
 * ActionsService's offer and recording screen-details operations; every seed
 * type the screen recognizes is now wired to real data.
 */
async function resolveContentDetailStreamingOptions(
  data: Partial<ContentDetailState>,
  seed: ContentDetailSeed,
  signal: AbortSignal
): Promise<void> {
  const action = (
    data.watchActions === undefined ? [] : data.watchActions
  ).find(
    (entry) =>
      entry.kernelType === ActionKernelType.StreamingOptionsActionKernel
  );
  if (action === undefined) {
    return;
  }
  try {
    const interactor = new VodInteractor();
    const contentId = readContentDetailActionString(action, "contentId");
    let details: Awaited<
      ReturnType<VodInteractor["fetchOttContentActionDetails"]>
    >;
    if (seed.seedType === CONTENT_DETAIL_SEED_TYPE.UNIFIED_COLLECTION_ID) {
      details = await interactor.getUnifiedCollectionActionDetails(
        { unifiedCollectionId: seed.seedId },
        true,
        { signal }
      );
    } else if (seed.seedType === CONTENT_DETAIL_SEED_TYPE.OFFER_ID) {
      details = await interactor.fetchOttOfferActionDetails(
        { offerId: seed.seedId, isCatchUp: false, isStartOver: false },
        true,
        { signal }
      );
    } else {
      details = await interactor.fetchOttContentActionDetails(
        { contentId: contentId !== undefined ? contentId : seed.seedId },
        true,
        { signal }
      );
    }
    applyContentDetailStreamingOptions(data, details);
  } catch (error: unknown) {
    Log.e(TAG, "resolveContentDetailStreamingOptions()", error);
  }
}

export const loadContentDetail = createAsyncThunk<
  LoadContentDetailPayload,
  LoadContentDetailArgs,
  { rejectValue: LoadContentDetailErrorPayload }
>(
  "contentDetail/loadContentDetail",
  async (args: LoadContentDetailArgs, { rejectWithValue, signal }) => {
    const { seedType, seedId } = args;
    const seed: ContentDetailSeed = { seedType, seedId };
    const startTimeMs: number = Date.now();

    if (seedId === "") {
      const message: string = "Missing object id";
      const logMessage: string = "loadContentDetail() " + message;
      Log.e(TAG, logMessage);
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        logMessage,
        DiagnosticSeverity.Warning,
        { event: "load_failed", reason: "missing_id", seed_type: seedType }
      );
      return rejectWithValue({ message, errorCode: "" });
    }

    try {
      const contentDetailInteractor = new ContentDetailInteractor();
      const previewInteractor = new PreviewInteractor();
      const imageHostBaseUrl: string = await resolveImageHostBaseUrl();

      let data: Partial<ContentDetailState>;
      let preview: TPreview;

      if (seedType === CONTENT_DETAIL_SEED_TYPE.UNIFIED_COLLECTION_ID) {
        const [screenDetails, collectionPreview, playableEpisodeActions] =
          await Promise.all([
            contentDetailInteractor.fetchUnifiedCollectionScreenDetails(
              seedId,
              true,
              { signal }
            ),
            previewInteractor.fetchUnifiedCollectionPreview(
              { unifiedCollectionId: seedId },
              true,
              { signal }
            ),
            contentDetailInteractor.resolvePlayableEpisodeActions(seedId, {
              signal
            })
          ]);
        preview = collectionPreview;
        data = mapUnifiedCollectionScreenDetailsToState(
          screenDetails,
          collectionPreview,
          playableEpisodeActions
        );
        applyPreviewRatings(data, preview);
        applyHeroImage(data, imageHostBaseUrl, seedId, "series");
      } else {
        const [screenDetails, contentPreview] = await Promise.all([
          contentDetailInteractor.fetchScreenDetailsForSeed(seed, true, {
            signal
          }),
          fetchPreviewForSeed(previewInteractor, seed, signal)
        ]);
        preview = contentPreview;
        const kind: ContentDetailKind = deriveContentKind(
          screenDetails.contentScreenType
        );
        data = mapContentScreenDetailsToState(
          screenDetails,
          contentPreview,
          kind
        );
        applyPreviewRatings(data, preview);

        // Offer / recording / VOD-offer seeds are not content ids; the
        // screen-details response names the content they resolve to.
        let objectId: string =
          screenDetails.contentId !== undefined &&
          screenDetails.contentId !== ""
            ? screenDetails.contentId
            : seedId;
        if (
          kind !== "episode" &&
          screenDetails.collectionId !== undefined &&
          screenDetails.collectionId !== ""
        ) {
          objectId = screenDetails.collectionId;
        }
        applyHeroImage(
          data,
          imageHostBaseUrl,
          objectId,
          toAtmosphericType(kind)
        );
      }

      await resolveContentDetailStreamingOptions(data, seed, signal);
      applyProviderLogos(data, imageHostBaseUrl);
      applyWatchSubtitleRatings(data);

      data.badges = buildBadges(data.rating, preview);
      data.upcomingOfferText = formatNextAiringText(
        preview,
        data.networkName,
        new Date()
      );

      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadContentDetail() succeeded",
        DiagnosticSeverity.Info,
        {
          event: "load_succeeded",
          seed_type: seedType,
          duration_ms: String(Date.now() - startTimeMs)
        }
      );
      return { seed, data };
    } catch (error: unknown) {
      const appError: AppError = ContentDetailErrorMapper.fromError(error);
      const errorCode: string = appError.getUIErrorwithSubErrorCode();
      const message: string =
        appError.getMessage().length > 0
          ? appError.getMessage()
          : "Failed to load content detail";
      Log.e(TAG, `loadContentDetail() code=${errorCode}`, error);
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadContentDetail() failed",
        DiagnosticSeverity.Error,
        {
          event: "load_failed",
          reason: "exception",
          seed_type: seedType,
          error_code: errorCode,
          duration_ms: String(Date.now() - startTimeMs)
        }
      );
      return rejectWithValue({ message, errorCode });
    }
  }
);

function hasContentDetailContextChanged(
  initial: ContentDetailState,
  current: ContentDetailState
): boolean {
  const sameSeed =
    initial.seed === null
      ? current.seed === null
      : current.seed !== null &&
        initial.seed.seedType === current.seed.seedType &&
        initial.seed.seedId === current.seed.seedId;
  const sameSheet =
    initial.sheet === null
      ? current.sheet === null
      : current.sheet !== null &&
        initial.sheet.sectionId === current.sheet.sectionId &&
        initial.sheet.itemId === current.sheet.itemId;
  return !sameSeed || !sameSheet;
}

/** Launches the selected watch target without navigating after its context changes. */
export const playContentDetailAction = createAsyncThunk<
  boolean,
  ContentDetailActionData,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/playAction",
  async (action, { dispatch, getState, rejectWithValue }) => {
    try {
      if (action.isDisabled) {
        return false;
      }
      const context = getState().contentDetail;
      const playable = await buildContentDetailPlayable(action);
      if (hasContentDetailContextChanged(context, getState().contentDetail)) {
        return false;
      }
      if (playable === null) {
        dispatch(
          showToastMessage(Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST))
        );
        return rejectWithValue("Missing or unsupported playback target");
      }
      await VideoPlaybackController.getInstance().startPlayback(playable);
      return !hasContentDetailContextChanged(context, getState().contentDetail);
    } catch (error: unknown) {
      Log.e(TAG, "playContentDetailAction()", error);
      dispatch(
        showToastMessage(Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST))
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Playback failed"
      );
    }
  }
);

export const prepareContentDetailRecording = createAsyncThunk<
  {
    alreadyScheduled: boolean;
    freeMinutes: number;
    hasInsufficientStorage: boolean;
    recordingValues?: Record<string, unknown>;
    contentId?: string;
    optionGroups?: ContentDetailRecordingOptionGroup[];
    alwaysShowOverlay?: boolean;
  },
  ContentDetailRecordingParams,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/prepareRecording",
  async (params, { dispatch, getState, rejectWithValue, signal }) => {
    if (params.offerId === undefined || params.offerId === "") {
      return rejectWithValue("Missing offerId");
    }
    try {
      const context = getState().contentDetail;
      const status =
        await new RecordingsStatusInteractor().fetchRecordingStatus({ signal });
      if (hasContentDetailContextChanged(context, getState().contentDetail)) {
        return rejectWithValue("Recording selection changed");
      }
      const existing = status.recordingStatus.find(
        (entry) => entry.offerId === params.offerId
      );
      if (
        existing !== undefined &&
        [
          LEGACY_RECORDING_STATUS.SCHEDULED,
          LEGACY_RECORDING_STATUS.IN_PROGRESS,
          LEGACY_RECORDING_STATUS.RECORDED,
          RecordingStatusV2.ScheduledAsSingleExplicit,
          RecordingStatusV2.ScheduledAsOnePass,
          RecordingStatusV2.InProgressRecording,
          RecordingStatusV2.Complete
        ].includes(existing.recordingStatus)
      ) {
        dispatch(
          recordingScheduled({
            offerId: params.offerId,
            contentId: existing.contentId,
            recordingStatus: existing.recordingStatus
          })
        );
        return {
          alreadyScheduled: true,
          freeMinutes: 0,
          hasInsufficientStorage: false
        };
      }
      const quota = await new MyShowsInteractor().fetchDiskSpace({ signal });
      const freeMinutes = Number.isFinite(quota.availableStorageMinutes)
        ? Math.max(0, quota.availableStorageMinutes)
        : 0;
      const duration =
        params.duration !== undefined && Number.isFinite(params.duration)
          ? Math.max(0, params.duration)
          : 0;
      const start =
        params.airingStartTime === undefined
          ? Number.NaN
          : Date.parse(params.airingStartTime);
      const elapsed = Number.isFinite(start)
        ? Math.max(0, (Date.now() - start) / MILLISECONDS_IN_A_SECOND)
        : 0;
      const requiredMinutes = Math.ceil(
        Math.max(0, duration - elapsed) / SECONDS_IN_A_MINUTE
      );
      const recordings = new RecordingsServiceInteractor();
      const contentId = await recordings.resolveOfferRecordingContentId(
        params.offerId,
        params.contentId,
        { signal }
      );
      const request = {
        offerId: params.offerId,
        contentId,
        channelId: params.channelId
      };
      const schedule = await recordings.fetchOfferRecordingSchedule(request, {
        signal
      });
      if (schedule.recordingValues === undefined) {
        return rejectWithValue("Missing recording defaults");
      }
      const optionGroups =
        schedule.alwaysShowOverlay === true
          ? buildContentDetailRecordingOptionGroups(
              await recordings.fetchOfferRecordingOptions(request, { signal })
            )
          : [];
      if (hasContentDetailContextChanged(context, getState().contentDetail)) {
        return rejectWithValue("Recording selection changed");
      }
      return {
        alreadyScheduled: false,
        freeMinutes,
        contentId,
        recordingValues: { ...schedule.recordingValues },
        optionGroups,
        alwaysShowOverlay: schedule.alwaysShowOverlay === true,
        hasInsufficientStorage:
          freeMinutes === 0 || freeMinutes < requiredMinutes
      };
    } catch (error: unknown) {
      Log.e(TAG, "prepareContentDetailRecording()", error);
      dispatch(
        showToastMessage(Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST))
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Recording preflight failed"
      );
    }
  },
  {
    condition: (_params, { getState }) =>
      !getState().contentDetail.isRecordingPending
  }
);

export const createContentDetailRecording = createAsyncThunk<
  void,
  ContentDetailRecordingParams,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/createRecording",
  async (
    params: ContentDetailRecordingParams,
    { dispatch, rejectWithValue, signal }
  ) => {
    if (params.offerId === undefined || params.offerId === "") {
      Log.w(TAG, "createContentDetailRecording() missing offerId");
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "createContentDetailRecording() missing offerId",
        DiagnosticSeverity.Warning,
        { event: "recording_failed", reason: "missing_offer_id" }
      );
      return rejectWithValue("Missing offerId");
    }
    try {
      // Fires `createRecording` on success — see
      // RecordingsServiceInteractor.createOfferRecording().
      await new RecordingsServiceInteractor().createOfferRecording(
        {
          offerId: params.offerId,
          contentId: params.contentId,
          channelNumber: params.channelNumber,
          channelId: params.channelId,
          airingStartTime: params.airingStartTime,
          recordingValues: params.recordingValues
        },
        { signal },
        { screen: CONTENT_DETAIL_SCREEN_NAME }
      );
    } catch (error: unknown) {
      // Scheduling failed — surface it instead of failing silently.
      Log.e(TAG, "createContentDetailRecording()", error);
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "createContentDetailRecording() failed",
        DiagnosticSeverity.Error,
        { event: "recording_failed", reason: "exception" }
      );
      dispatch(
        showToastMessage(
          recordingActionErrorMapper.appendErrorCode(
            Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST),
            error
          )
        )
      );
      OperationalLogger.logUiError({
        errorCode: recordingActionErrorMapper.toCompositeCode(error),
        errorMessage: OperationalLogger.describeErrorMessage(error),
        screen: CONTENT_DETAIL_SCREEN_NAME,
        screenId: CONTENT_DETAIL_SCREEN_ID
      });
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to schedule recording"
      );
    }

    DiagnosticLogger.log(
      "content_detail_lifecycle",
      "createContentDetailRecording() succeeded",
      DiagnosticSeverity.Info,
      { event: "recording_created" }
    );

    dispatch(
      recordingScheduled({
        offerId: params.offerId,
        contentId: params.contentId
      })
    );
    return undefined;
  },
  {
    condition: (_params, { getState }) =>
      !getState().contentDetail.isRecordingPending
  }
);

/**
 * Toast for a rail item's sheet failing to load — a lightweight,
 * non-blocking notice carrying the A716 code, not the blocking dialog the
 * main screen load uses (same distinction iptv makes between the two).
 *
 * Dispatched by {@link loadContentDetailSheetData} when either the selected
 * item's screen-details or preview request fails.
 */
export function contentDetailSheetErrorToast(
  error: unknown
): ReturnType<typeof showToastMessage> {
  return showToastMessage(getContentDetailSheetErrorMessage(error));
}

/** Result of loading one selected explore item's actions sheet. */
interface LoadContentDetailSheetPayload {
  sectionId: ContentDetailSectionId;
  itemId: string;
  title: string;
  subtitle?: string;
  metaLine?: string;
  description?: string;
  imageUrl?: string;
  cast?: string;
  schedule?: string;
  badges?: string[];
  providerLogoUrl?: string;
  watchActions?: ContentDetailActionData[];
  scheduleActions?: ContentDetailActionData[];
  actions: ContentDetailActionData[];
}

/** Joins the non-empty metadata segments the sheet renders below its title. */
function joinSheetMetaLine(
  segments: Array<string | undefined>
): string | undefined {
  const values: string[] = segments.filter(
    (segment: string | undefined): segment is string =>
      segment !== undefined && segment.length > 0
  );
  return values.length > 0 ? values.join(" · ") : undefined;
}

/** Reads a string preview field without depending on its generated DTO shape. */
function readSheetPreviewString(
  preview: TPreview,
  key: string
): string | undefined {
  const value: unknown = (preview as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function resolveSheetAtmosphericImageObjectId(
  kind: ContentDetailKind,
  preview: TPreview,
  contentId: string
): string {
  if (kind !== "movie") {
    return contentId;
  }
  const collectionId: string | undefined = readSheetPreviewString(
    preview,
    "collectionId"
  );
  return collectionId !== undefined ? collectionId : contentId;
}

function resolveSheetProviderLogo(
  mappedData: Partial<ContentDetailState>
): string | undefined {
  if (mappedData.networkLogoUrl !== undefined) {
    return mappedData.networkLogoUrl;
  }
  if (
    mappedData.streamingServices !== undefined &&
    mappedData.streamingServices.length > 0
  ) {
    return mappedData.streamingServices[0].logoUrl;
  }
  return undefined;
}

function resolveSheetCast(
  mappedData: Partial<ContentDetailState>
): string | undefined {
  if (mappedData.cast !== undefined && mappedData.cast.length > 0) {
    return mappedData.cast.join(", ");
  }
  return undefined;
}

/**
 * Loads metadata and action kernels for a selected carousel item.
 *
 * Upcoming cards use their offer id for screen details but retain their
 * content id for Preview; all other currently selectable rails use a content
 * id for both calls.
 */
export const loadContentDetailSheetData = createAsyncThunk<
  LoadContentDetailSheetPayload,
  LoadContentDetailSheetParams,
  { rejectValue: string }
>(
  "contentDetail/loadContentDetailSheetData",
  async (
    params: LoadContentDetailSheetParams,
    { dispatch, rejectWithValue, signal }
  ) => {
    const isCollection: boolean =
      params.itemId.startsWith("tivo:cl.") || params.itemType === "Series";
    const screenDetailsSeed: ContentDetailSeed = {
      seedType:
        params.sectionId === "upcoming"
          ? CONTENT_DETAIL_SEED_TYPE.OFFER_ID
          : CONTENT_DETAIL_SEED_TYPE.CONTENT_ID,
      seedId: params.itemId
    };
    const previewContentId: string =
      params.contentId !== undefined && params.contentId !== ""
        ? params.contentId
        : params.itemId;

    try {
      const contentDetailInteractor = new ContentDetailInteractor();
      const previewInteractor = new PreviewInteractor();
      const imageHostBaseUrlPromise: Promise<string> =
        resolveImageHostBaseUrl();
      let preview: TPreview;
      let mappedData: Partial<ContentDetailState>;
      let kind: ContentDetailKind;

      if (isCollection) {
        const [screenDetails, collectionPreview, imageHostBaseUrl] =
          await Promise.all([
            contentDetailInteractor.fetchUnifiedCollectionScreenDetails(
              params.itemId,
              true,
              { signal }
            ),
            previewInteractor.fetchUnifiedCollectionPreview(
              { unifiedCollectionId: params.itemId },
              true,
              { signal }
            ),
            imageHostBaseUrlPromise
          ]);
        preview = collectionPreview;
        mappedData = mapUnifiedCollectionScreenDetailsToState(
          screenDetails,
          collectionPreview,
          null
        );
        const imageUrl: string = buildAtmosphericImageUrl({
          baseImageUrl: imageHostBaseUrl,
          objectId: params.itemId,
          contentType: "series",
          width: ATMOSPHERIC_IMAGE_WIDTH,
          height: ATMOSPHERIC_IMAGE_HEIGHT
        });
        mappedData.heroImageUrl = imageUrl !== "" ? imageUrl : undefined;
      } else {
        const [screenDetails, contentPreview, imageHostBaseUrl] =
          await Promise.all([
            contentDetailInteractor.fetchScreenDetailsForSeed(
              screenDetailsSeed,
              true,
              { signal }
            ),
            previewInteractor.fetchContentPreviewV3(
              { contentId: previewContentId },
              true,
              { signal }
            ),
            imageHostBaseUrlPromise
          ]);
        preview = contentPreview;
        kind = deriveContentKind(screenDetails.contentScreenType);
        mappedData = mapContentScreenDetailsToState(
          screenDetails,
          contentPreview,
          kind
        );
        const imageUrl: string = buildAtmosphericImageUrl({
          baseImageUrl: imageHostBaseUrl,
          objectId: resolveSheetAtmosphericImageObjectId(
            kind,
            contentPreview,
            previewContentId
          ),
          contentType: toAtmosphericType(kind),
          width: ATMOSPHERIC_IMAGE_WIDTH,
          height: ATMOSPHERIC_IMAGE_HEIGHT
        });
        mappedData.heroImageUrl = imageUrl !== "" ? imageUrl : undefined;
      }
      await resolveContentDetailStreamingOptions(
        mappedData,
        isCollection
          ? {
              seedType: CONTENT_DETAIL_SEED_TYPE.UNIFIED_COLLECTION_ID,
              seedId: params.itemId
            }
          : screenDetailsSeed,
        signal
      );
      applyProviderLogos(mappedData, await imageHostBaseUrlPromise);
      applyWatchSubtitleRatings(mappedData);
      applyPreviewRatings(mappedData, preview);
      const badges = buildBadges(mappedData.rating, preview);
      const actions: ContentDetailActionData[] = [
        ...(mappedData.watchActions !== undefined
          ? mappedData.watchActions
          : []),
        ...(mappedData.scheduleActions !== undefined
          ? mappedData.scheduleActions
          : [])
      ];
      const genres: string | undefined = formatGenres(mappedData.genres);
      const displayBadgeLabels: string[] = badges.map(
        (badge): string => badge.label
      );
      let sheetFeedItemType: string | undefined = params.itemType;
      if (
        sheetFeedItemType === undefined &&
        mappedData.kind !== undefined &&
        mappedData.kind !== null
      ) {
        sheetFeedItemType = mappedData.kind;
      }
      const contentTypeLabel: string = getContentDetailSheetTypeLabel({
        previewType: readSheetPreviewString(preview, "previewType"),
        seasonNumber: params.seasonNumber,
        episodeNumber: params.episodeNumber,
        feedItemType: sheetFeedItemType,
        itemId: params.itemId
      });
      const isMovie: boolean = mappedData.kind === "movie";
      const seedSubtitle: string | undefined =
        params.seedSubtitle !== undefined &&
        params.seedSubtitle.toLowerCase() !== contentTypeLabel.toLowerCase()
          ? params.seedSubtitle
          : undefined;
      const subtitle: string | undefined = joinSheetMetaLine([
        contentTypeLabel,
        isMovie ? mappedData.releaseYear : undefined,
        mappedData.duration,
        seedSubtitle,
        params.seedMetaLine
      ]);
      const mainTitle: string = getSheetMainTitle({
        title:
          mappedData.title !== undefined && mappedData.title !== ""
            ? mappedData.title
            : params.seedTitle,
        episodeTitle: params.episodeTitle,
        previewType: readSheetPreviewString(preview, "previewType"),
        seasonNumber: params.seasonNumber,
        episodeNumber: params.episodeNumber,
        feedItemType: params.itemType,
        itemId: params.itemId
      });

      return {
        sectionId: params.sectionId,
        itemId: params.itemId,
        title: mainTitle,
        subtitle,
        metaLine: genres,
        description: mappedData.description,
        imageUrl: mappedData.heroImageUrl,
        cast: resolveSheetCast(mappedData),
        schedule: mappedData.schedule,
        badges: displayBadgeLabels,
        providerLogoUrl: resolveSheetProviderLogo(mappedData),
        watchActions: mappedData.watchActions,
        scheduleActions: mappedData.scheduleActions,
        actions
      };
    } catch (error: unknown) {
      Log.e(TAG, "loadContentDetailSheetData()", error);
      dispatch(contentDetailSheetErrorToast(error));
      return rejectWithValue(getContentDetailSheetErrorMessage(error));
    }
  }
);

export const updateContentDetailFavorite = createAsyncThunk<
  void,
  ContentDetailActionData,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/updateFavorite",
  async (action, { dispatch, getState, signal, rejectWithValue }) => {
    try {
      const target = getContentDetailFavoriteTarget(action);
      if (action.isDisabled || target === null) {
        return rejectWithValue("Missing favorite target");
      }
      const { seed, sheet } = getState().contentDetail;
      await new ContentDetailInteractor().updateFavorite(target, { signal });
      const current = getState().contentDetail;
      if (
        seed !== null &&
        current.seed !== null &&
        seed.seedId === current.seed.seedId &&
        seed.seedType === current.seed.seedType
      ) {
        await dispatch(loadContentDetail({ ...seed, isRefresh: true }));
      }
      const latest = getState().contentDetail;
      if (
        sheet !== null &&
        latest.sheet !== null &&
        latest.sheet.sectionId === sheet.sectionId &&
        latest.sheet.itemId === sheet.itemId
      ) {
        await dispatch(
          loadContentDetailSheetData({
            ...(sheet.loadParams !== undefined
              ? sheet.loadParams
              : {
                  sectionId: sheet.sectionId,
                  itemId: sheet.itemId,
                  seedTitle: sheet.title
                }),
            isRefresh: true
          })
        );
      }
    } catch (error: unknown) {
      Log.e(TAG, "updateContentDetailFavorite()", error);
      dispatch(
        showToastMessage(Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST))
      );
      return rejectWithValue(
        error instanceof Error ? error.message : "Favorite update failed"
      );
    }
  },
  {
    condition: (_action, { getState }) =>
      !getState().contentDetail.isFavoritePending
  }
);

// ─── Explore-section thunks ───

/** Result of the Episodes section's initial (seasons + first page) load. */
function mapEpisodeGuidePage(
  page: EpisodeInfo[],
  seasonOrYear: number,
  imageHostBaseUrl: string
): ContentDetailEpisodeData[] {
  return page.map((episode) => ({
    ...mapEpisode(episode, imageHostBaseUrl),
    id: String(seasonOrYear) + ":" + episode.contentId,
    contentId: episode.contentId,
    seasonOrYear
  }));
}

export function resolveNextEpisodeGuideSeason(
  section: ContentDetailEpisodeGuideState,
  index: number
): ContentDetailSeasonData | undefined {
  const canLoad = (season: ContentDetailSeasonData): boolean =>
    section.itemsBySeason[season.id] === undefined ||
    section.hasMoreBySeason[season.id] !== false;
  const item = section.items[index];
  const keySeason =
    item === undefined ? Number.NaN : Number(item.id.split(":")[0]);
  let seasonOrYear = item === undefined ? undefined : item.seasonOrYear;
  if (Number.isFinite(keySeason)) {
    seasonOrYear = keySeason;
  }
  const visibleSeason = section.seasons.find(
    (season) => season.seasonOrYear === seasonOrYear
  );
  return visibleSeason !== undefined && canLoad(visibleSeason)
    ? visibleSeason
    : section.seasons.find(canLoad);
}

interface EpisodeGuideSeasonsPayload {
  seasons: ContentDetailSeasonData[];
  grouping?: string;
  selectedSeasonOrYear: number | null;
  items: ContentDetailEpisodeData[];
  hasMoreItems: boolean;
}

/** Result of one Episodes page load. */
interface EpisodeGuidePagePayload {
  seasonOrYear: number;
  items: ContentDetailEpisodeData[];
  hasMoreItems: boolean;
  /** True to replace the season's cached page rather than append to it. */
  resetItems: boolean;
}

/**
 * Result of one page load for an offset-paginated section. Upcoming and May
 * Also Like both page an offset/limit list into this exact shape, differing
 * only in what fetches the page and how `hasMoreItems` is derived — see
 * {@link fetchSectionPage} and {@link applySectionPage}.
 */
interface SectionPagePayload<TItem> {
  items: TItem[];
  hasMoreItems: boolean;
  resetItems: boolean;
}

/** Result of one Upcoming page load. */
type UpcomingPagePayload = SectionPagePayload<ContentDetailUpcomingData>;

/**
 * Loads the Episodes section: the season list, then the first page of the
 * newest season. Mirrors `loadEpisodeGuideSectionState`.
 *
 * Guarded by `condition` so an already-loaded or in-flight section is never
 * refetched — Android's `if (currentState is Content || Loading) return`.
 */
export const loadEpisodeGuideSeasons = createAsyncThunk<
  EpisodeGuideSeasonsPayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadEpisodeGuideSeasons",
  async (_arg: void, { getState, rejectWithValue, signal }) => {
    const objectId: string | null =
      getState().contentDetail.episodeGuideObjectId;
    if (objectId === null) {
      return rejectWithValue("Missing episode guide object id");
    }

    try {
      const interactor = new EpisodeGuideInteractor();
      const imageHostBaseUrl: string = await resolveImageHostBaseUrl();
      const seasonsData: SeasonsData = await interactor.fetchSeasons(objectId, {
        signal
      });
      const seasons: ContentDetailSeasonData[] = mapSeasons(seasonsData);
      if (seasons.length === 0) {
        DiagnosticLogger.log(
          "content_detail_lifecycle",
          "loadEpisodeGuideSeasons() succeeded",
          DiagnosticSeverity.Info,
          { event: "episode_seasons_loaded", season_count: "0" }
        );
        return {
          seasons,
          grouping: seasonsData.grouping,
          selectedSeasonOrYear: null,
          items: [],
          hasMoreItems: false
        };
      }

      const selectedSeasonOrYear: number = seasons[0].seasonOrYear;
      const page: EpisodeInfo[] = await interactor.fetchEpisodes(
        objectId,
        selectedSeasonOrYear,
        EPISODE_GUIDE_PAGE_SIZE,
        0,
        { signal }
      );

      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadEpisodeGuideSeasons() succeeded",
        DiagnosticSeverity.Info,
        {
          event: "episode_seasons_loaded",
          season_count: String(seasons.length)
        }
      );
      return {
        seasons,
        grouping: seasonsData.grouping,
        selectedSeasonOrYear,
        items: mapEpisodeGuidePage(
          page,
          selectedSeasonOrYear,
          imageHostBaseUrl
        ),
        hasMoreItems: page.length === EPISODE_GUIDE_PAGE_SIZE
      };
    } catch (error: unknown) {
      Log.e(TAG, "loadEpisodeGuideSeasons()", error);
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadEpisodeGuideSeasons() failed",
        DiagnosticSeverity.Error,
        { event: "episode_seasons_load_failed" }
      );
      return rejectWithValue(getSectionErrorMessage());
    }
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const section = getState().contentDetail.episodeGuide;
      return section.status !== "content" && section.status !== "loading";
    }
  }
);

/** Arguments for one Episodes page fetch. */
export interface LoadEpisodeGuidePageArgs {
  seasonOrYear: number;
  /** True when the season has no cached page yet, so offset restarts at 0. */
  resetItems: boolean;
}

/**
 * Loads one page of episodes for a single season. Mirrors
 * `ExploreEpisodeGuideViewModel.loadSeasonPage`.
 *
 * `condition` doubles as Android's pagination mutex: a second page request
 * while one is already in flight is dropped rather than queued.
 */
export const loadEpisodeGuideSeasonPage = createAsyncThunk<
  EpisodeGuidePagePayload,
  LoadEpisodeGuidePageArgs,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadEpisodeGuideSeasonPage",
  async (
    args: LoadEpisodeGuidePageArgs,
    { getState, rejectWithValue, signal }
  ) => {
    const { episodeGuideObjectId, episodeGuide } = getState().contentDetail;
    if (episodeGuideObjectId === null) {
      return rejectWithValue("Missing episode guide object id");
    }

    const cached: ContentDetailEpisodeData[] | undefined =
      episodeGuide.itemsBySeason[String(args.seasonOrYear)];
    const offset: number =
      args.resetItems || cached === undefined ? 0 : cached.length;

    try {
      const interactor = new EpisodeGuideInteractor();
      const imageHostBaseUrl: string = await resolveImageHostBaseUrl();
      const page: EpisodeInfo[] = await interactor.fetchEpisodes(
        episodeGuideObjectId,
        args.seasonOrYear,
        EPISODE_GUIDE_PAGE_SIZE,
        offset,
        { signal }
      );
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadEpisodeGuideSeasonPage() succeeded",
        DiagnosticSeverity.Info,
        {
          event: "episode_page_loaded",
          season_or_year: String(args.seasonOrYear),
          item_count: String(page.length)
        }
      );
      return {
        seasonOrYear: args.seasonOrYear,
        items: mapEpisodeGuidePage(page, args.seasonOrYear, imageHostBaseUrl),
        hasMoreItems: page.length === EPISODE_GUIDE_PAGE_SIZE,
        resetItems: args.resetItems
      };
    } catch (error: unknown) {
      Log.e(TAG, "loadEpisodeGuideSeasonPage()", error);
      DiagnosticLogger.log(
        "content_detail_lifecycle",
        "loadEpisodeGuideSeasonPage() failed",
        DiagnosticSeverity.Error,
        {
          event: "episode_page_load_failed",
          season_or_year: String(args.seasonOrYear)
        }
      );
      return rejectWithValue(getSectionErrorMessage());
    }
  },
  {
    condition: (args: LoadEpisodeGuidePageArgs, { getState }): boolean => {
      const section = getState().contentDetail.episodeGuide;
      return (
        section.status === "content" &&
        !section.isLoadingMore &&
        section.seasons.some(
          (season) => season.seasonOrYear === args.seasonOrYear
        )
      );
    }
  }
);

/**
 * Fetches one page for an offset-paginated Content Detail section and logs
 * success/failure the same way every such section's thunks do. Returns
 * `null` on failure so the caller can `rejectWithValue` with its own
 * user-facing message — `createAsyncThunk`'s `rejectWithValue` has to be
 * called from inside the thunk itself, so this helper cannot call it.
 */
async function fetchSectionPage<TPayload extends { items: unknown[] }>(
  fetchPage: () => Promise<TPayload>,
  logTag: string,
  eventBase: string
): Promise<TPayload | null> {
  try {
    const result: TPayload = await fetchPage();
    DiagnosticLogger.log(
      "content_detail_lifecycle",
      logTag + "() succeeded",
      DiagnosticSeverity.Info,
      { event: eventBase + "_loaded", item_count: String(result.items.length) }
    );
    return result;
  } catch (error: unknown) {
    Log.e(TAG, logTag + "()", error);
    DiagnosticLogger.log(
      "content_detail_lifecycle",
      logTag + "() failed",
      DiagnosticSeverity.Error,
      { event: eventBase + "_load_failed" }
    );
    return null;
  }
}

/**
 * Loads the first page of the Upcoming section. Mirrors
 * `ExploreUpcomingViewModel.loadIfNeeded`.
 */
export const loadUpcomingSection = createAsyncThunk<
  UpcomingPagePayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadUpcomingSection",
  async (_arg: void, { getState, rejectWithValue, signal }) => {
    const objectId: string | null = getState().contentDetail.upcomingObjectId;
    if (objectId === null) {
      return rejectWithValue("Missing upcoming object id");
    }
    const result: UpcomingPagePayload | null = await fetchSectionPage(
      () => fetchUpcomingPage(objectId, 0, true, signal),
      "loadUpcomingSection",
      "upcoming"
    );
    return result === null ? rejectWithValue(getSectionErrorMessage()) : result;
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const section = getState().contentDetail.upcoming;
      return section.status !== "content" && section.status !== "loading";
    }
  }
);

/** Loads the next page of the Upcoming section. */
export const loadUpcomingNextPage = createAsyncThunk<
  UpcomingPagePayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadUpcomingNextPage",
  async (_arg: void, { getState, rejectWithValue, signal }) => {
    const { upcomingObjectId, upcoming } = getState().contentDetail;
    if (upcomingObjectId === null) {
      return rejectWithValue("Missing upcoming object id");
    }
    const result: UpcomingPagePayload | null = await fetchSectionPage(
      () =>
        fetchUpcomingPage(
          upcomingObjectId,
          upcoming.items.length,
          false,
          signal
        ),
      "loadUpcomingNextPage",
      "upcoming_next_page"
    );
    return result === null ? rejectWithValue(getSectionErrorMessage()) : result;
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const section = getState().contentDetail.upcoming;
      return (
        section.status === "content" &&
        section.hasMoreItems &&
        !section.isLoadingMore
      );
    }
  }
);

/**
 * Shared fetch body for both Upcoming thunks.
 *
 * `minEndTime` is "now", so an airing already in progress is still returned
 * but past ones are not — the same bound Android passes
 * (`minEndTime = Clock.System.now()`).
 */
async function fetchUpcomingPage(
  objectId: string,
  offset: number,
  resetItems: boolean,
  signal: AbortSignal
): Promise<UpcomingPagePayload> {
  const interactor = new UpcomingOffersInteractor();
  const imageHostBaseUrl: string = await resolveImageHostBaseUrl();
  const response: UpcomingOfferListV2 = await interactor.fetchUpcomingOffers(
    objectId,
    new Date().toISOString(),
    UPCOMING_OFFERS_PAGE_SIZE,
    offset,
    { signal }
  );
  const offers = response.upcomingOffers;
  const items: ContentDetailUpcomingData[] =
    offers === undefined
      ? []
      : offers.map((offer) =>
          mapUpcomingOffer(offer, imageHostBaseUrl, formatClockTime)
        );
  return {
    items,
    hasMoreItems: items.length === UPCOMING_OFFERS_PAGE_SIZE,
    resetItems
  };
}

/** Payload shared by both May Also Like thunks. */
type MayAlsoLikePagePayload = SectionPagePayload<ContentDetailRecommendedData>;

/**
 * Loads the first page of the May Also Like section. Mirrors
 * `loadUpcomingSection`.
 */
export const loadMayAlsoLikeSection = createAsyncThunk<
  MayAlsoLikePagePayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadMayAlsoLikeSection",
  async (_arg: void, { getState, rejectWithValue, signal }) => {
    const objectId: string | null =
      getState().contentDetail.mayAlsoLikeObjectId;
    if (objectId === null) {
      return rejectWithValue("Missing may-also-like object id");
    }
    const result: MayAlsoLikePagePayload | null = await fetchSectionPage(
      () => fetchMoreLikeThisPage(objectId, 0, true, signal),
      "loadMayAlsoLikeSection",
      "may_also_like"
    );
    return result === null ? rejectWithValue(getSectionErrorMessage()) : result;
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const section = getState().contentDetail.mayAlsoLike;
      return section.status !== "content" && section.status !== "loading";
    }
  }
);

/** Loads the next page of the May Also Like section. */
export const loadMayAlsoLikeNextPage = createAsyncThunk<
  MayAlsoLikePagePayload,
  void,
  { state: UnityRootState; rejectValue: string }
>(
  "contentDetail/loadMayAlsoLikeNextPage",
  async (_arg: void, { getState, rejectWithValue, signal }) => {
    const { mayAlsoLikeObjectId, mayAlsoLike } = getState().contentDetail;
    if (mayAlsoLikeObjectId === null) {
      return rejectWithValue("Missing may-also-like object id");
    }
    const result: MayAlsoLikePagePayload | null = await fetchSectionPage(
      () =>
        fetchMoreLikeThisPage(
          mayAlsoLikeObjectId,
          mayAlsoLike.items.length,
          false,
          signal
        ),
      "loadMayAlsoLikeNextPage",
      "may_also_like_next_page"
    );
    return result === null ? rejectWithValue(getSectionErrorMessage()) : result;
  },
  {
    condition: (_arg: void, { getState }): boolean => {
      const section = getState().contentDetail.mayAlsoLike;
      return (
        section.status === "content" &&
        section.hasMoreItems &&
        !section.isLoadingMore
      );
    }
  }
);

/**
 * Shared fetch body for both May Also Like thunks.
 *
 * `hasMoreItems` follows the same rule Android's `MoreLikeThisSectionLoader`
 * uses: `offset + pageItems.length < resolvedCount`, treating a missing
 * `resolvedCount` as `0` — pagination stops on the server's declared total,
 * not on "this page came back shorter than requested".
 */
async function fetchMoreLikeThisPage(
  objectId: string,
  offset: number,
  resetItems: boolean,
  signal: AbortSignal
): Promise<MayAlsoLikePagePayload> {
  const interactor = new ShowflixCarouselInteractor();
  const imageHostBaseUrl: string = await resolveImageHostBaseUrl();
  const response = await interactor.fetchMoreLikeThis(
    {
      feedItemId: objectId,
      count: MORE_LIKE_THIS_PAGE_SIZE,
      offset
    },
    { signal }
  );
  const items: ContentDetailRecommendedData[] = response.items.map((item) =>
    mapMoreLikeThisItem(item, imageHostBaseUrl)
  );
  const resolvedCount: number =
    response.resolvedCount !== undefined ? response.resolvedCount : 0;
  return {
    items,
    hasMoreItems: offset + items.length < resolvedCount,
    resetItems
  };
}

// ─── Reducer helpers ───

/**
 * Merges a mapper's `Partial<ContentDetailState>` into the draft state,
 * defaulting every array field to `[]` and every optional scalar to
 * `undefined` when absent from `data` — this is always a *full* replace
 * (not an incremental patch) so switching seeds never leaves stale data
 * (e.g. a previous title's cast list) visible under the new seed.
 */
function applyMappedData(
  state: ContentDetailState,
  data: Partial<ContentDetailState>
): void {
  state.kind = data.kind !== undefined ? data.kind : null;
  state.title = data.title;
  state.contentType = data.contentType;
  state.rating = data.rating;
  state.starRating = data.starRating !== undefined ? data.starRating : null;
  state.releaseYear = data.releaseYear;
  state.duration = data.duration;
  state.genres = data.genres !== undefined ? data.genres : [];
  state.description = data.description;
  state.descriptionMaxLines = data.descriptionMaxLines;
  state.cast = data.cast !== undefined ? data.cast : [];
  state.heroImageUrl = data.heroImageUrl;
  state.heroImageAlt = data.heroImageAlt;

  state.watchActions = data.watchActions !== undefined ? data.watchActions : [];
  state.streamingServices =
    data.streamingServices !== undefined ? data.streamingServices : [];
  state.upcomingOfferText = data.upcomingOfferText;

  state.schedule = data.schedule;
  state.badges = data.badges !== undefined ? data.badges : [];
  state.networkLogoUrl = data.networkLogoUrl;
  state.networkName = data.networkName;

  state.scheduleActions =
    data.scheduleActions !== undefined ? data.scheduleActions : [];

  state.sections = data.sections !== undefined ? data.sections : [];
  state.episodeGuideObjectId =
    data.episodeGuideObjectId !== undefined ? data.episodeGuideObjectId : null;
  state.upcomingObjectId =
    data.upcomingObjectId !== undefined ? data.upcomingObjectId : null;
  state.mayAlsoLikeObjectId =
    data.mayAlsoLikeObjectId !== undefined ? data.mayAlsoLikeObjectId : null;
}

/**
 * Resets every section to "hidden", then marks the ones this title actually
 * has as ready to load. A section whose id is absent from `sections` stays
 * hidden and renders nothing — Android's `withUiSelections`.
 */
function resetSections(state: ContentDetailState): void {
  state.episodeGuide = emptyEpisodeGuide();
  state.upcoming = emptyUpcoming();
  state.castMembers = emptySection();
  state.mayAlsoLike = emptySection();

  // Cast has no service yet (Android comments it out of both tab maps), so it
  // resolves straight to "empty" rather than pretending a fetch is coming.
  // May Also Like does have a service now — it stays "hidden" here and is
  // loaded lazily by `loadMayAlsoLikeSection`, same as Episodes/Upcoming.
  for (const section of state.sections) {
    if (section.id === "cast") {
      state.castMembers.status = "empty";
    }
  }
}

/**
 * Rebuilds the Episodes rail from the per-season cache: the concatenation of
 * every cached season in season (picker) order. Android's
 * `buildEpisodeGuideItems`.
 */
function buildEpisodeGuideItems(
  section: ContentDetailEpisodeGuideState
): ContentDetailEpisodeData[] {
  const items: ContentDetailEpisodeData[] = [];
  for (const season of section.seasons) {
    const cached: ContentDetailEpisodeData[] | undefined =
      section.itemsBySeason[season.id];
    if (cached !== undefined) {
      items.push(...cached);
    }
  }
  return items;
}

/**
 * True when any season is either uncached or known to have another page.
 * Android's `hasMoreEpisodeGuideItems`.
 */
function hasMoreEpisodeGuideItems(
  section: ContentDetailEpisodeGuideState
): boolean {
  return section.seasons.some((season: ContentDetailSeasonData): boolean => {
    const isCached: boolean = section.itemsBySeason[season.id] !== undefined;
    const hasMore: boolean = section.hasMoreBySeason[season.id] !== false;
    return !isCached || hasMore;
  });
}

/** Clamps `value` into `[0, max]`, treating a negative `max` as 0. */
function clamp(value: number, max: number): number {
  if (max < 0) {
    return 0;
  }
  return Math.min(Math.max(0, value), max);
}

// ─── Slice ───

function updateEpisodeGuideSeasonFromFocus(state: ContentDetailState): void {
  const section = state.sections[state.focusedSectionIndex];
  if (
    state.focusedRow !== "sections" ||
    state.focusedSectionArea !== "rail" ||
    section === undefined ||
    section.id !== "episodes"
  ) {
    return;
  }
  const item = state.episodeGuide.items[state.focusedItemIndex];
  if (item === undefined) {
    return;
  }
  const keySeason = Number(item.id.split(":")[0]);
  const season = Number.isFinite(keySeason) ? keySeason : item.seasonOrYear;
  if (
    state.episodeGuide.seasons.some((entry) => entry.seasonOrYear === season)
  ) {
    state.episodeGuide.selectedSeasonOrYear =
      season === undefined ? null : season;
  }
}

const contentDetailSlice = createSlice({
  name: "contentDetail",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Selects a season in a section's picker. The rail is not filtered — the
     * selection bumps `seasonScrollRequestToken` so the view scrolls to that
     * season's first tile, matching Android's `handleSeasonSelected`.
     */
    setActiveSeason(
      state,
      action: PayloadAction<{
        sectionId: ContentDetailSectionId;
        seasonOrYear: number;
      }>
    ): void {
      const { sectionId, seasonOrYear } = action.payload;
      const section =
        sectionId === "upcoming" ? state.upcoming : state.episodeGuide;
      const isKnown: boolean = section.seasons.some(
        (season: ContentDetailSeasonData): boolean =>
          season.seasonOrYear === seasonOrYear
      );
      if (!isKnown) {
        return;
      }
      if (
        sectionId === "upcoming" &&
        section.selectedSeasonOrYear === seasonOrYear
      ) {
        return;
      }
      section.selectedSeasonOrYear = seasonOrYear;
      section.seasonScrollRequestToken += 1;
    },
    setFocusedRow(state, action: PayloadAction<ContentDetailFocusedRow>): void {
      state.focusedRow = action.payload;
    },
    setFocusedActionIndex(state, action: PayloadAction<number>): void {
      state.focusedActionIndex = action.payload;
    },
    setFocusedSection(
      state,
      action: PayloadAction<{
        sectionIndex: number;
        area?: ContentDetailSectionArea;
        itemIndex?: number;
      }>
    ): void {
      const { sectionIndex, area, itemIndex } = action.payload;
      state.focusedRow = "sections";
      state.focusedSectionIndex = clamp(
        sectionIndex,
        state.sections.length - 1
      );
      if (area !== undefined) {
        state.focusedSectionArea = area;
      }
      state.focusedItemIndex = itemIndex !== undefined ? itemIndex : 0;
      updateEpisodeGuideSeasonFromFocus(state);
    },
    setFocusedItemIndex(state, action: PayloadAction<number>): void {
      state.focusedItemIndex = Math.max(0, action.payload);
      updateEpisodeGuideSeasonFromFocus(state);
    },
    openContentDetailSheet(
      state,
      action: PayloadAction<ContentDetailSheetState>
    ): void {
      state.sheet = action.payload;
    },
    setSheetFocusedActionIndex(state, action: PayloadAction<number>): void {
      if (state.sheet === null) {
        return;
      }
      state.sheet.focusedActionIndex = clamp(
        action.payload,
        state.sheet.actions.length - 1
      );
    },
    closeContentDetailSheet(state): void {
      state.sheet = null;
    },
    resetContentDetailState(): ContentDetailState {
      return {
        ...INITIAL_STATE,
        episodeGuide: emptyEpisodeGuide(),
        upcoming: emptyUpcoming(),
        castMembers: emptySection(),
        mayAlsoLike: emptySection()
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(recordingScheduled, (state, action): void => {
        state.scheduleActions = applyContentDetailScheduleSuccess(
          state.scheduleActions,
          action.payload,
          state.seed
        );
        if (state.sheet !== null) {
          const scheduleActions = applyContentDetailScheduleSuccess(
            state.sheet.scheduleActions !== undefined
              ? state.sheet.scheduleActions
              : state.sheet.actions.filter(
                  (entry) => entry.variant === "secondary"
                ),
            action.payload,
            null
          );
          state.sheet.scheduleActions = scheduleActions;
          state.sheet.actions = (
            state.sheet.watchActions !== undefined
              ? state.sheet.watchActions
              : state.sheet.actions.filter(
                  (entry) => entry.variant === "primary"
                )
          ).concat(scheduleActions);
          state.sheet.focusedActionIndex = clamp(
            state.sheet.focusedActionIndex,
            state.sheet.actions.length - 1
          );
        }
      })
      .addCase(updateContentDetailFavorite.pending, (state): void => {
        state.isFavoritePending = true;
      })
      .addCase(updateContentDetailFavorite.fulfilled, (state): void => {
        state.isFavoritePending = false;
      })
      .addCase(updateContentDetailFavorite.rejected, (state): void => {
        state.isFavoritePending = false;
      })
      .addCase(prepareContentDetailRecording.pending, (state): void => {
        state.isRecordingPending = true;
      })
      .addCase(prepareContentDetailRecording.fulfilled, (state): void => {
        state.isRecordingPending = false;
      })
      .addCase(prepareContentDetailRecording.rejected, (state): void => {
        state.isRecordingPending = false;
      })
      .addCase(createContentDetailRecording.pending, (state): void => {
        state.isRecordingPending = true;
      })
      .addCase(createContentDetailRecording.fulfilled, (state): void => {
        state.isRecordingPending = false;
      })
      .addCase(createContentDetailRecording.rejected, (state): void => {
        state.isRecordingPending = false;
      })
      .addCase(loadContentDetail.pending, (state, action): void => {
        const isRefresh: boolean = action.meta.arg.isRefresh === true;
        if (!isRefresh) {
          state.episodeGuide.requestId = undefined;
        }
        state.isLoading = !isRefresh;
        state.isRefreshing = isRefresh;
        state.error = null;
        state.errorCode = "";
      })
      .addCase(loadContentDetail.fulfilled, (state, action): void => {
        const { seed, data } = action.payload;
        const isRefresh: boolean =
          action.meta.arg.isRefresh === true &&
          state.seed !== null &&
          state.seed.seedId === seed.seedId &&
          state.seed.seedType === seed.seedType;
        applyMappedData(state, data);
        state.seed = seed;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
        state.errorCode = "";
        if (isRefresh) {
          const actionCount =
            state.focusedRow === "schedule"
              ? state.scheduleActions.length
              : state.watchActions.length + state.streamingServices.length;
          state.focusedActionIndex = clamp(
            state.focusedActionIndex,
            actionCount - 1
          );
          return;
        }
        resetSections(state);
        state.focusedRow = "watch";
        state.focusedActionIndex = 0;
        state.focusedSectionIndex = 0;
        state.focusedSectionArea = "rail";
        state.focusedItemIndex = 0;
        state.sheet = null;
      })
      .addCase(loadContentDetail.rejected, (state, action): void => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.error =
          action.payload !== undefined
            ? action.payload.message
            : "Unknown error";
        state.errorCode =
          action.payload !== undefined ? action.payload.errorCode : "";
      })

      // ── Selected-item actions sheet ──
      .addCase(loadContentDetailSheetData.pending, (state, action): void => {
        const params: LoadContentDetailSheetParams = action.meta.arg;
        if (
          params.isRefresh &&
          state.sheet !== null &&
          state.sheet.itemId === params.itemId
        ) {
          return;
        }
        state.sheet = {
          loadParams: params,
          sectionId: params.sectionId,
          itemId: params.itemId,
          title: params.seedTitle !== undefined ? params.seedTitle : "",
          subtitle: params.seedSubtitle,
          metaLine: params.seedMetaLine,
          isLoading: true,
          error: null,
          actions: [],
          focusedActionIndex: 0
        };
      })
      .addCase(loadContentDetailSheetData.fulfilled, (state, action): void => {
        const payload: LoadContentDetailSheetPayload = action.payload;
        if (
          state.sheet === null ||
          state.sheet.sectionId !== payload.sectionId ||
          state.sheet.itemId !== payload.itemId
        ) {
          return;
        }
        state.sheet = {
          ...state.sheet,
          title: payload.title,
          subtitle: payload.subtitle,
          metaLine: payload.metaLine,
          description: payload.description,
          imageUrl: payload.imageUrl,
          cast: payload.cast,
          schedule: payload.schedule,
          badges: payload.badges,
          providerLogoUrl: payload.providerLogoUrl,
          watchActions: payload.watchActions,
          scheduleActions: payload.scheduleActions,
          isLoading: false,
          error: null,
          actions: payload.actions,
          focusedActionIndex: action.meta.arg.isRefresh
            ? clamp(state.sheet.focusedActionIndex, payload.actions.length - 1)
            : 0
        };
      })
      .addCase(loadContentDetailSheetData.rejected, (state, action): void => {
        const params: LoadContentDetailSheetParams = action.meta.arg;
        if (
          state.sheet === null ||
          state.sheet.sectionId !== params.sectionId ||
          state.sheet.itemId !== params.itemId
        ) {
          return;
        }
        state.sheet.isLoading = false;
        if (params.isRefresh) {
          return;
        }
        state.sheet.error =
          action.payload !== undefined
            ? action.payload
            : "Failed to load content detail sheet";
      })

      // ── Episodes section ──
      .addCase(loadEpisodeGuideSeasons.pending, (state, action): void => {
        state.episodeGuide.status = "loading";
        state.episodeGuide.error = null;
        state.episodeGuide.requestId = action.meta.requestId;
      })
      .addCase(loadEpisodeGuideSeasons.fulfilled, (state, action): void => {
        if (state.episodeGuide.requestId !== action.meta.requestId) {
          return;
        }
        const payload: EpisodeGuideSeasonsPayload = action.payload;
        const section: ContentDetailEpisodeGuideState = state.episodeGuide;
        section.requestId = undefined;
        section.seasons = payload.seasons;
        section.grouping = payload.grouping;
        section.selectedSeasonOrYear = payload.selectedSeasonOrYear;
        section.error = null;
        section.isLoadingMore = false;

        if (payload.seasons.length === 0) {
          // Carousels hide silently on an empty response too — no inline
          // empty-state message, same as a failed load.
          section.status = "hidden";
          section.items = [];
          section.hasMoreItems = false;
          return;
        }

        const seasonKey: string = String(payload.selectedSeasonOrYear);
        section.itemsBySeason = { [seasonKey]: payload.items };
        section.hasMoreBySeason = { [seasonKey]: payload.hasMoreItems };
        section.items = buildEpisodeGuideItems(section);
        section.hasMoreItems = hasMoreEpisodeGuideItems(section);
        section.status = "content";
      })
      .addCase(loadEpisodeGuideSeasons.rejected, (state, action): void => {
        if (state.episodeGuide.requestId !== action.meta.requestId) {
          return;
        }
        state.episodeGuide.requestId = undefined;
        state.episodeGuide.status = "error";
        state.episodeGuide.isLoadingMore = false;
        state.episodeGuide.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      })
      .addCase(loadEpisodeGuideSeasonPage.pending, (state, action): void => {
        state.episodeGuide.isLoadingMore = true;
        state.episodeGuide.requestId = action.meta.requestId;
      })
      .addCase(loadEpisodeGuideSeasonPage.fulfilled, (state, action): void => {
        if (state.episodeGuide.requestId !== action.meta.requestId) {
          return;
        }
        const payload: EpisodeGuidePagePayload = action.payload;
        const section: ContentDetailEpisodeGuideState = state.episodeGuide;
        section.requestId = undefined;
        const key: string = String(payload.seasonOrYear);
        const existing: ContentDetailEpisodeData[] =
          payload.resetItems || section.itemsBySeason[key] === undefined
            ? []
            : section.itemsBySeason[key];

        section.itemsBySeason[key] = existing.concat(payload.items);
        section.hasMoreBySeason[key] = payload.hasMoreItems;
        section.items = buildEpisodeGuideItems(section);
        section.hasMoreItems = hasMoreEpisodeGuideItems(section);
        section.isLoadingMore = false;
        section.error = null;
        if (
          payload.resetItems &&
          section.selectedSeasonOrYear === payload.seasonOrYear
        ) {
          section.seasonScrollRequestToken += 1;
        }
        if (section.items.length > 0) {
          section.status = "content";
        }
      })
      .addCase(loadEpisodeGuideSeasonPage.rejected, (state, action): void => {
        const section: ContentDetailEpisodeGuideState = state.episodeGuide;
        if (section.requestId !== action.meta.requestId) {
          return;
        }
        section.requestId = undefined;
        section.isLoadingMore = false;
        section.status = "error";
        section.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      })

      // ── Upcoming section ──
      .addCase(loadUpcomingSection.pending, (state): void => {
        state.upcoming.status = "loading";
        state.upcoming.error = null;
      })
      .addCase(loadUpcomingSection.fulfilled, (state, action): void => {
        applyUpcomingPage(state.upcoming, action.payload);
      })
      .addCase(loadUpcomingSection.rejected, (state, action): void => {
        // Carousels degrade silently — see loadEpisodeGuideSeasons.rejected.
        state.upcoming.status = "hidden";
        state.upcoming.isLoadingMore = false;
        state.upcoming.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      })
      .addCase(loadUpcomingNextPage.pending, (state): void => {
        state.upcoming.isLoadingMore = true;
      })
      .addCase(loadUpcomingNextPage.fulfilled, (state, action): void => {
        applyUpcomingPage(state.upcoming, action.payload);
      })
      .addCase(loadUpcomingNextPage.rejected, (state, action): void => {
        state.upcoming.isLoadingMore = false;
        state.upcoming.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      })

      // ── May Also Like section ──
      .addCase(loadMayAlsoLikeSection.pending, (state): void => {
        state.mayAlsoLike.status = "loading";
        state.mayAlsoLike.error = null;
      })
      .addCase(loadMayAlsoLikeSection.fulfilled, (state, action): void => {
        applyMoreLikeThisPage(state.mayAlsoLike, action.payload);
      })
      .addCase(loadMayAlsoLikeSection.rejected, (state, action): void => {
        // Carousels degrade silently — see loadEpisodeGuideSeasons.rejected.
        state.mayAlsoLike.status = "hidden";
        state.mayAlsoLike.isLoadingMore = false;
        state.mayAlsoLike.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      })
      .addCase(loadMayAlsoLikeNextPage.pending, (state): void => {
        state.mayAlsoLike.isLoadingMore = true;
      })
      .addCase(loadMayAlsoLikeNextPage.fulfilled, (state, action): void => {
        applyMoreLikeThisPage(state.mayAlsoLike, action.payload);
      })
      .addCase(loadMayAlsoLikeNextPage.rejected, (state, action): void => {
        state.mayAlsoLike.isLoadingMore = false;
        state.mayAlsoLike.error =
          action.payload !== undefined
            ? action.payload
            : getSectionErrorMessage();
      });
  }
});

/**
 * Applies one offset-paginated page (initial or subsequent) to a section's
 * state — shared by Upcoming and May Also Like. Returns the merged item list
 * so a caller with extra per-section derivation (Upcoming's season chips)
 * can build on it without recomputing the reset-vs-append logic.
 */
function applySectionPage<TItem>(
  section: ContentDetailSectionState<TItem>,
  payload: SectionPagePayload<TItem>
): TItem[] {
  const items: TItem[] = payload.resetItems
    ? payload.items
    : section.items.concat(payload.items);
  section.items = items;
  section.hasMoreItems = payload.hasMoreItems;
  section.isLoadingMore = false;
  section.error = null;
  // Hides silently on an empty response too — no inline empty-state message,
  // same as a failed load.
  section.status = items.length === 0 ? "hidden" : "content";
  return items;
}

/** Applies one May Also Like page (initial or subsequent) to the section state. */
function applyMoreLikeThisPage(
  section: ContentDetailSectionState<ContentDetailRecommendedData>,
  payload: MayAlsoLikePagePayload
): void {
  applySectionPage(section, payload);
}

/** Applies one Upcoming page (initial or subsequent) to the section state. */
function applyUpcomingPage(
  section: ContentDetailUpcomingState,
  payload: UpcomingPagePayload
): void {
  const items: ContentDetailUpcomingData[] = applySectionPage(section, payload);
  section.seasons = deriveUpcomingSeasons(items);
  if (section.selectedSeasonOrYear === null && section.seasons.length > 0) {
    section.selectedSeasonOrYear = section.seasons[0].seasonOrYear;
  }
}

export const {
  setActiveSeason,
  setFocusedRow,
  setFocusedActionIndex,
  setFocusedSection,
  setFocusedItemIndex,
  openContentDetailSheet,
  setSheetFocusedActionIndex,
  closeContentDetailSheet,
  resetContentDetailState
} = contentDetailSlice.actions;

// ─── Selectors ───

export const selectContentDetailSeed = (
  state: UnityRootState
): ContentDetailState["seed"] => state.contentDetail.seed;

export const selectContentDetailKind = (
  state: UnityRootState
): ContentDetailState["kind"] => state.contentDetail.kind;

export const selectContentDetailIsLoading = (state: UnityRootState): boolean =>
  state.contentDetail.isLoading;

export const selectContentDetailIsRefreshing = (
  state: UnityRootState
): boolean => state.contentDetail.isRefreshing;

export const selectContentDetailError = (
  state: UnityRootState
): string | null => state.contentDetail.error;

export const selectContentDetailErrorCode = (state: UnityRootState): string =>
  state.contentDetail.errorCode;

export const selectContentDetailTitle = (
  state: UnityRootState
): string | undefined => state.contentDetail.title;

export const selectContentDetailContentType = (
  state: UnityRootState
): string | undefined => state.contentDetail.contentType;

export const selectContentDetailRating = (
  state: UnityRootState
): string | undefined => state.contentDetail.rating;

export const selectContentDetailStarRating = (
  state: UnityRootState
): number | null => state.contentDetail.starRating;

export const selectContentDetailReleaseYear = (
  state: UnityRootState
): string | undefined => state.contentDetail.releaseYear;

export const selectContentDetailDuration = (
  state: UnityRootState
): string | undefined => state.contentDetail.duration;

export const selectContentDetailGenres = (state: UnityRootState): string[] =>
  state.contentDetail.genres;

export const selectContentDetailDescription = (
  state: UnityRootState
): string | undefined => state.contentDetail.description;

export const selectContentDetailDescriptionMaxLines = (
  state: UnityRootState
): number | undefined => state.contentDetail.descriptionMaxLines;

export const selectContentDetailCast = (state: UnityRootState): string[] =>
  state.contentDetail.cast;

export const selectContentDetailHeroImageUrl = (
  state: UnityRootState
): string | undefined => state.contentDetail.heroImageUrl;

export const selectContentDetailHeroImageAlt = (
  state: UnityRootState
): string | undefined => state.contentDetail.heroImageAlt;

export const selectContentDetailWatchActions = (
  state: UnityRootState
): ContentDetailState["watchActions"] => state.contentDetail.watchActions;

export const selectContentDetailStreamingServices = (
  state: UnityRootState
): ContentDetailState["streamingServices"] =>
  state.contentDetail.streamingServices;

export const selectContentDetailUpcomingOfferText = (
  state: UnityRootState
): string | undefined => state.contentDetail.upcomingOfferText;

export const selectContentDetailSchedule = (
  state: UnityRootState
): string | undefined => state.contentDetail.schedule;

export const selectContentDetailBadges = (
  state: UnityRootState
): ContentDetailState["badges"] => state.contentDetail.badges;

export const selectContentDetailNetworkLogoUrl = (
  state: UnityRootState
): string | undefined => state.contentDetail.networkLogoUrl;

export const selectContentDetailNetworkName = (
  state: UnityRootState
): string | undefined => state.contentDetail.networkName;

export const selectContentDetailScheduleActions = (
  state: UnityRootState
): ContentDetailState["scheduleActions"] => state.contentDetail.scheduleActions;

export const selectContentDetailSections = (
  state: UnityRootState
): ContentDetailSectionData[] => state.contentDetail.sections;

export const selectEpisodeGuideObjectId = (
  state: UnityRootState
): string | null => state.contentDetail.episodeGuideObjectId;

export const selectUpcomingObjectId = (state: UnityRootState): string | null =>
  state.contentDetail.upcomingObjectId;

export const selectMayAlsoLikeObjectId = (
  state: UnityRootState
): string | null => state.contentDetail.mayAlsoLikeObjectId;

export const selectContentDetailEpisodeGuide = (
  state: UnityRootState
): ContentDetailEpisodeGuideState => state.contentDetail.episodeGuide;

export const selectContentDetailUpcoming = (
  state: UnityRootState
): ContentDetailUpcomingState => state.contentDetail.upcoming;

export const selectContentDetailCastMembers = (
  state: UnityRootState
): ContentDetailState["castMembers"] => state.contentDetail.castMembers;

export const selectContentDetailMayAlsoLike = (
  state: UnityRootState
): ContentDetailState["mayAlsoLike"] => state.contentDetail.mayAlsoLike;

export const selectContentDetailFocusedRow = (
  state: UnityRootState
): ContentDetailFocusedRow => state.contentDetail.focusedRow;

export const selectContentDetailFocusedActionIndex = (
  state: UnityRootState
): number => state.contentDetail.focusedActionIndex;

export const selectContentDetailFocusedSectionIndex = (
  state: UnityRootState
): number => state.contentDetail.focusedSectionIndex;

export const selectContentDetailFocusedSectionArea = (
  state: UnityRootState
): ContentDetailSectionArea => state.contentDetail.focusedSectionArea;

export const selectContentDetailFocusedItemIndex = (
  state: UnityRootState
): number => state.contentDetail.focusedItemIndex;

export const selectContentDetailSheet = (
  state: UnityRootState
): ContentDetailSheetState | null => state.contentDetail.sheet;

export default contentDetailSlice.reducer;
